import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import api, { API, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronRight, FileText, FileSpreadsheet, FileDown, Star, Play, Lock, Bookmark, Trash2, Info } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { HelpLink } from "@/components/HelpHint";

// Reports that have a matching Help centre article.
const REPORT_HELP = { "salary-cost": "employer-ni" };

const fmtCell = (v, t) => {
  if (v === null || v === undefined || v === "") return "";
  if (t === "date") { try { return fmtDate(v); } catch { return v; } }
  if (t === "currency") return `£${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  if (t === "percent") return `${Number(v).toFixed(1)}%`;
  return String(v);
};

const Crumb = ({ name }) => (
  <div className="flex items-center gap-1.5 text-sm text-slate-500" data-testid="report-breadcrumb">
    <Link to="/reports" className="hover:text-[#2EA3FD]">Reports</Link>
    {name && <><ChevronRight size={14} /><span className="text-[#0B1A2F] font-medium">{name}</span></>}
  </div>
);

export default function ReportScreen() {
  const { key } = useParams();
  const [sp] = useSearchParams();
  const [meta, setMeta] = useState(null);
  const [years, setYears] = useState([]);
  const [f, setF] = useState({});
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [views, setViews] = useState([]);
  const [activeView, setActiveView] = useState("");
  const [drill, setDrill] = useState(null);
  const [subjects, setSubjects] = useState([]);

  const loadViews = useCallback(() => api.get(`/report-hub/views/${key}`).then((r) => setViews(r.data)).catch(() => setViews([])), [key]);

  useEffect(() => { api.get("/review-types").then((r) => setSubjects(r.data)).catch(() => setSubjects([])); }, []);

  useEffect(() => {
    // Deep-link: seed filters from the URL query string.
    const urlF = {}; sp.forEach((v, k) => { if (k !== "format") urlF[k] = v; });
    api.get("/report-hub/catalog").then((r) => setMeta(r.data.tiles.find((t) => t.key === key) || false)).catch(() => setMeta(false));
    api.get("/holiday-years").then((r) => {
      const ys = r.data || []; const today = new Date().toISOString().slice(0, 10);
      const active = ys.find((y) => y.start_date <= today && today <= y.end_date) || ys[0];
      setYears(ys); setF((x) => ({ year: active?.id || "", ...x, ...urlF }));
    }).catch(() => setF((x) => ({ ...x, ...urlF })));
    api.get(`/report-hub/views/${key}`).then((r) => {
      setViews(r.data);
      if (Object.keys(urlF).length === 0) {
        const def = r.data.find((v) => v.is_default);
        if (def) { setF((x) => ({ ...x, ...def.filters })); setActiveView(def.id); }
      }
    }).catch(() => {});
  }, [key, sp]);

  const qs = useCallback(() => {
    const p = new URLSearchParams();
    (meta?.filters || []).forEach((k) => { if (f[k]) p.set(k, f[k]); });
    return p.toString();
  }, [meta, f]);

  const run = async () => {
    setLoading(true);
    try { const r = await api.get(`/report-hub/run/${key}?${qs()}`); setRes(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not run report"); }
    finally { setLoading(false); }
  };
  const dl = (fmt) => window.open(`${API}/report-hub/run/${key}?format=${fmt}&${qs()}`, "_blank");
  const favorite = async () => { try { await api.put(`/report-hub/favorites/${key}`, { favorite: !meta.favorite }); setMeta({ ...meta, favorite: !meta.favorite }); } catch {} };

  const applyView = (id) => {
    setActiveView(id);
    const v = views.find((x) => x.id === id);
    if (v) setF((x) => ({ ...x, ...v.filters }));
  };
  const saveView = async () => {
    const name = window.prompt("Name this view");
    if (!name) return;
    const filters = {}; (meta.filters || []).forEach((k) => { if (f[k]) filters[k] = f[k]; });
    const def = window.confirm("Set this as your default view for this report?");
    try { const r = await api.post(`/report-hub/views/${key}`, { name, filters, is_default: def }); toast.success("View saved"); await loadViews(); setActiveView(r.data.id); }
    catch { toast.error("Could not save view"); }
  };
  const deleteView = async () => {
    if (!activeView) return;
    try { await api.delete(`/report-hub/views/${activeView}`); toast.success("View removed"); setActiveView(""); await loadViews(); } catch {}
  };

  if (meta === null) return <div className="text-slate-400">Loading…</div>;
  if (meta === false) return <div className="space-y-4"><Crumb /><div className="bg-white border rounded-xl p-10 text-center"><Lock className="mx-auto text-slate-300 mb-2" /><p className="font-semibold text-[#0B1A2F]">Report not available</p><p className="text-sm text-slate-500 mt-1">You don't have access to this report, or it doesn't exist.</p></div></div>;

  const flt = meta.filters || [];
  return (
    <div className="space-y-5" data-testid="report-screen">
      <Crumb name={meta.name} />
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold text-[#0B1A2F] flex items-center gap-2">{meta.name}{meta.sensitive && <span className="text-[10px] uppercase tracking-wider bg-rose-100 text-rose-700 rounded-full px-2 py-0.5 flex items-center gap-1"><Lock size={10} />Confidential</span>}</h1>
          <p className="text-sm text-slate-500 mt-0.5">{meta.desc}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {REPORT_HELP[key] && <HelpLink article={REPORT_HELP[key]} testid="report-help-link" />}
          <Button variant="outline" size="sm" onClick={favorite} data-testid="report-screen-fav"><Star size={14} className={`mr-1.5 ${meta.favorite ? "fill-amber-400 text-amber-400" : ""}`} />{meta.favorite ? "Favourited" : "Save as favourite"}</Button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 flex flex-wrap items-end gap-3" data-testid="report-filters">
        {flt.includes("period") && (
          <div><label className="text-xs text-slate-500 block mb-1">Reporting period</label>
            <Select value={f.period || "full_year"} onValueChange={(v) => setF({ ...f, period: v })}>
              <SelectTrigger className="h-9 w-52" data-testid="filter-period"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="full_year">Full year (annual)</SelectItem>
                <SelectItem value="this_month">This month</SelectItem>
                <SelectItem value="this_quarter">This quarter</SelectItem>
                <SelectItem value="tax_year">Tax year</SelectItem>
                <SelectItem value="calendar_year">Calendar year</SelectItem>
                <SelectItem value="holiday_year">Holiday year</SelectItem>
                <SelectItem value="custom">Custom date range</SelectItem>
              </SelectContent>
            </Select></div>)}
        {flt.includes("year") && (
          <div><label className="text-xs text-slate-500 block mb-1">Leave year</label>
            <Select value={f.year || ""} onValueChange={(v) => setF({ ...f, year: v })}>
              <SelectTrigger className="h-9 w-48" data-testid="filter-year"><SelectValue placeholder="Year" /></SelectTrigger>
              <SelectContent>{years.map((y) => <SelectItem key={y.id} value={y.id}>{y.name}</SelectItem>)}</SelectContent>
            </Select></div>)}
        {flt.includes("department") && <div><label className="text-xs text-slate-500 block mb-1">Department</label><Input className="h-9 w-44" value={f.department || ""} onChange={(e) => setF({ ...f, department: e.target.value })} placeholder="All" data-testid="filter-department" /></div>}
        {flt.includes("status") && <div><label className="text-xs text-slate-500 block mb-1">Records</label>
          <Select value={f.status || "active"} onValueChange={(v) => setF({ ...f, status: v })}><SelectTrigger className="h-9 w-36" data-testid="filter-status"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="all">All</SelectItem></SelectContent></Select></div>}
        {flt.includes("as_at") && <div><label className="text-xs text-slate-500 block mb-1">As at</label><Input type="date" className="h-9 w-44" value={f.as_at || ""} onChange={(e) => setF({ ...f, as_at: e.target.value })} data-testid="filter-asat" /></div>}
        {flt.includes("days") && <div><label className="text-xs text-slate-500 block mb-1">Expiry window</label>
          <Select value={f.days || "60"} onValueChange={(v) => setF({ ...f, days: v })}><SelectTrigger className="h-9 w-40" data-testid="filter-days"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="30">30 days</SelectItem><SelectItem value="60">60 days</SelectItem><SelectItem value="90">90 days</SelectItem></SelectContent></Select></div>}
        {flt.includes("date_from") && (!flt.includes("period") || f.period === "custom") && <div><label className="text-xs text-slate-500 block mb-1">From</label><Input type="date" className="h-9 w-44" value={f.date_from || ""} onChange={(e) => setF({ ...f, date_from: e.target.value })} data-testid="filter-from" /></div>}
        {flt.includes("date_to") && (!flt.includes("period") || f.period === "custom") && <div><label className="text-xs text-slate-500 block mb-1">To</label><Input type="date" className="h-9 w-44" value={f.date_to || ""} onChange={(e) => setF({ ...f, date_to: e.target.value })} data-testid="filter-to" /></div>}
        {flt.includes("subject") && (
          <div><label className="text-xs text-slate-500 block mb-1">Subject</label>
            <Select value={f.subject || "all"} onValueChange={(v) => setF({ ...f, subject: v })}>
              <SelectTrigger className="h-9 w-52" data-testid="filter-subject"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All subjects</SelectItem>
                {subjects.filter((s) => !["probation", "pip"].includes(s.key)).map((s) => <SelectItem key={s.key} value={s.key}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select></div>)}
        {flt.includes("group") && (
          <div><label className="text-xs text-slate-500 block mb-1">Group by</label>
            <Select value={f.group || "employee"} onValueChange={(v) => setF({ ...f, group: v })}>
              <SelectTrigger className="h-9 w-40" data-testid="filter-group"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="employee">Employee</SelectItem>
                <SelectItem value="team">Team</SelectItem>
              </SelectContent>
            </Select></div>)}
        <Button className="bg-[#2EA3FD] hover:bg-[#1B8EE6] h-9" onClick={run} disabled={loading} data-testid="report-run"><Play size={14} className="mr-1.5" />{loading ? "Running…" : "Run report"}</Button>
        {res && <>
          <Button variant="outline" className="h-9" onClick={() => dl("pdf")} data-testid="report-dl-pdf"><FileText size={14} className="mr-1.5" />PDF</Button>
          <Button variant="outline" className="h-9" onClick={() => dl("xlsx")} data-testid="report-dl-xlsx"><FileSpreadsheet size={14} className="mr-1.5" />Excel</Button>
          <Button variant="outline" className="h-9" onClick={() => dl("csv")} data-testid="report-dl-csv"><FileDown size={14} className="mr-1.5" />CSV</Button>
        </>}
      </div>

      {flt.length > 0 && (
        <div className="flex flex-wrap items-center gap-2" data-testid="report-views">
          <span className="text-xs text-slate-400">Saved views:</span>
          <Select value={activeView} onValueChange={applyView}>
            <SelectTrigger className="h-8 w-52 text-xs" data-testid="view-select"><SelectValue placeholder={views.length ? "Choose a saved view" : "No saved views yet"} /></SelectTrigger>
            <SelectContent>{views.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}{v.is_default ? " ★" : ""}</SelectItem>)}</SelectContent>
          </Select>
          <Button variant="outline" size="sm" className="h-8" onClick={saveView} data-testid="view-save"><Bookmark size={13} className="mr-1" />Save current view</Button>
          {activeView && <Button variant="ghost" size="sm" className="h-8 text-rose-500" onClick={deleteView} data-testid="view-delete"><Trash2 size={13} /></Button>}
        </div>
      )}

      {!meta.available ? (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-sm text-amber-800" data-testid="report-planned">This report is on the CASS reporting roadmap and will be enabled in an upcoming phase.</div>
      ) : !res ? (
        <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-sm text-slate-400" data-testid="report-idle">Select <strong className="text-[#0B1A2F]">Run report</strong> to see a preview.</div>
      ) : (
        <div className="space-y-3" data-testid="report-preview">
          <div className="flex items-center justify-between text-xs text-slate-400">
            <span data-testid="report-count">{res.count} row{res.count === 1 ? "" : "s"}</span>
            <span>Generated {fmtDate(res.generated_at)}{res.meta ? ` · ${res.meta.map((m) => `${m[0]}: ${m[1]}`).join(" · ")}` : ""}</span>
          </div>
          {res.summary && res.summary.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="report-summary">
              {res.summary.map((s, i) => <div key={i} className="bg-white border border-slate-200 rounded-lg p-3"><p className="text-xs text-slate-500">{s[0]}</p><p className="text-lg font-bold text-[#0B1A2F]">{String(s[1])}</p></div>)}
            </div>
          )}
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-x-auto">
            {res.count === 0 ? <p className="text-sm text-slate-400 p-8 text-center">No data found for these filters.</p> : (
              <table className="w-full text-sm">
                <thead className="bg-[#0B1A2F] text-white"><tr>{res.columns.map((c) => <th key={c.key} className="text-left px-3 py-2 font-medium whitespace-nowrap">{c.label}</th>)}</tr></thead>
                <tbody>
                  {res.rows.slice(0, 200).map((r, i) => <tr key={i} onClick={() => r._detail && setDrill(r._detail)} className={`border-t border-slate-50 even:bg-slate-50/40 ${r._detail ? "cursor-pointer hover:bg-sky-50" : ""}`} data-testid={`report-row-${i}`}>{res.columns.map((c, ci) => <td key={c.key} className="px-3 py-2 text-slate-700 whitespace-nowrap">{ci === 0 && r._detail ? <span className="inline-flex items-center gap-1">{fmtCell(r[c.key], c.type)}<Info size={12} className="text-[#2EA3FD]" /></span> : fmtCell(r[c.key], c.type)}</td>)}</tr>)}
                  {res.totals && <tr className="border-t-2 border-slate-200 bg-[#EFF8FF] font-semibold">{res.columns.map((c, i) => <td key={c.key} className="px-3 py-2 text-[#0B1A2F]">{res.totals[c.key] != null ? fmtCell(res.totals[c.key], c.type) : (i === 0 ? "Total" : "")}</td>)}</tr>}
                </tbody>
              </table>
            )}
          </div>
          {res.count > 200 && <p className="text-xs text-slate-400">Showing first 200 rows on screen — download for the full report.</p>}
        </div>
      )}

      <Dialog open={!!drill} onOpenChange={(o) => { if (!o) setDrill(null); }}>
        <DialogContent data-testid="report-drill-dialog">
          <DialogHeader><DialogTitle>{drill?.title || "Calculation"}</DialogTitle></DialogHeader>
          <div className="divide-y divide-slate-100">
            {(drill?.lines || []).map((ln, i) => (
              <div key={i} className="flex items-center justify-between py-2 text-sm" data-testid={`drill-line-${i}`}>
                <span className="text-slate-500">{ln[0]}</span>
                <span className="font-semibold text-[#0B1A2F]">{fmtCell(ln[1], ln[2])}</span>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
